import {
  jb_richtext_css_default
} from "./chunk.XX6A3FFV.js";
import {
  reset_css_default
} from "./chunk.LJHCRQCZ.js";
import {
  initializeLink
} from "./chunk.534YP5ZJ.js";
import {
  customElement
} from "./chunk.QAGUCU4C.js";
import {
  Initiable
} from "./chunk.6DHNIVN5.js";
import {
  Spaceable
} from "./chunk.JI7MO3NT.js";
import {
  ChangesBackgroundColor
} from "./chunk.XYH4LHGJ.js";
import {
  replaceTag
} from "./chunk.JTCOOS2A.js";
import {
  A,
  b,
  i
} from "./chunk.BPIW577J.js";
import {
  n
} from "./chunk.XW2RSTES.js";
import {
  r
} from "./chunk.775UL347.js";
import {
  __decorateClass
} from "./chunk.LUIRGYUH.js";

// src/components/jb-richtext/jb-richtext.ts
var JbRichtext = class extends Initiable(ChangesBackgroundColor(Spaceable(i))) {
  async firstUpdated() {
    this._processSlotContent();
    new MutationObserver((mutations) => {
      if (mutations.some((m) => m.addedNodes.length > 0)) {
        this._processSlotContent();
      }
    }).observe(this, { childList: true });
  }
  _getContentSpacingProperties() {
    return { grid: true, type: "text", colsMdUp: 8 };
  }
  _processSlotContent() {
    const firstChildElement = [...Array.from(this.childNodes)].find((node) => node.nodeType === 1);
    if (firstChildElement === void 0) {
      return;
    }
    const slot = this.shadowRoot?.querySelector("slot");
    if (!slot) return;
    slot.append(...Array.from(this.childNodes));
    slot.querySelectorAll("table").forEach((table) => {
      const originalTable = table.cloneNode(true);
      table.replaceWith(
        this._wrapTable(
          originalTable,
          table.previousElementSibling,
          table.nextElementSibling
        )
      );
    });
    slot.querySelectorAll("a").forEach((link) => {
      initializeLink(link);
      link.dataset.flyPadding = "0";
    });
    this._updateHeadings();
  }
  _renderCustomStyles() {
    if (!this.customCss) return A;
    return b`<style>
      ${r(this.customCss)}
    </style>`;
  }
  /**
   * Ranks the headings converted out of the rich text below the level the component is announced at.
   *
   * Which tag an editor picked says how the heading looks, not how deep it sits: they reach for an
   * `h4` to get a small heading in a field that carries no `h2` above it. The depth therefore comes
   * from the levels the text itself uses — the shallowest one starts at the surrounding rank and
   * every further one goes a single step deeper — so a rich text cannot open a hole in the outline
   * of the page it sits on. Content whose levels are absolute rather than an editor's choice belongs
   * in a `jb-heading-context` that pins the rank around it.
   */
  _updateConvertedHeadingOffsets() {
    const headings = Array.from(
      this.shadowRoot?.querySelector("slot")?.querySelectorAll("jb-heading[data-source-heading-level]") ?? []
    );
    const levelsUsed = [...new Set(headings.map((heading) => Number(heading.dataset.sourceHeadingLevel)))].sort(
      (first, second) => first - second
    );
    headings.forEach((heading) => {
      const offset = levelsUsed.indexOf(Number(heading.dataset.sourceHeadingLevel));
      heading.setAttribute("offset", offset.toString());
    });
  }
  _updateHeadings() {
    const slot = this.shadowRoot.querySelector("slot");
    const headingConfig = [
      { selector: "h1", sourceLevel: 1, variant: "title-overview" },
      { selector: "h2", sourceLevel: 2, variant: "heading-lg" },
      { selector: "h3", sourceLevel: 3, variant: "heading-md" },
      { selector: "h4", sourceLevel: 4, variant: "heading-sm" }
    ];
    headingConfig.forEach(({ selector, sourceLevel, variant }) => {
      slot.querySelectorAll(selector).forEach((element) => {
        replaceTag(
          element,
          "jb-heading",
          /* @__PURE__ */ new Map([
            ["data-source-heading-level", sourceLevel.toString()],
            ["variant", variant]
          ])
        );
      });
    });
    this._updateConvertedHeadingOffsets();
  }
  _wrapTable(table, preface, footnotes) {
    const tableWrapper = document.createElement("div");
    tableWrapper.classList.add("tablewrapper");
    if (preface?.classList.contains("preface")) {
      tableWrapper.appendChild(preface);
    }
    if (footnotes?.tagName === "FIGCAPTION") {
      tableWrapper.appendChild(footnotes);
    }
    tableWrapper.appendChild(table);
    if (footnotes?.classList.contains("footnotes")) {
      tableWrapper.appendChild(footnotes);
    }
    return tableWrapper;
  }
  render() {
    return b`
      <slot class="${this.size}"></slot>
      ${this._renderCustomStyles()}
    `;
  }
};
JbRichtext.styles = [reset_css_default, jb_richtext_css_default];
__decorateClass([
  n()
], JbRichtext.prototype, "customCss", 2);
__decorateClass([
  n()
], JbRichtext.prototype, "size", 2);
JbRichtext = __decorateClass([
  customElement("jb-richtext")
], JbRichtext);

export {
  JbRichtext
};
